/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  useController,
  Controller,
  useFormContext,
  useWatch
} from 'react-hook-form';
import Select, { components } from 'react-select';
import {
  Form,
  Col,
  ToggleButton,
  ButtonGroup,
  Button,
  Row
} from 'react-bootstrap';
import axios from 'axios';
import apiClient from 'api/client';
import ToggleWithDropdown from './FormFields/ToggleWithDropdown';
import DynamicDropDown from './FormFields/DropDown';
import CheckBox from './FormFields/CheckBox';
import File from './FormFields/File';
import Dob from './FormFields/Dob';
import MapModel from './FormFields/Mapmodel';
import * as turf from '@turf/turf';
import { fetchAddressFromOla } from './FormFields/Mapmodel/services/fetchAddressfromOla';
import { FormLabel } from './FormFields/FormLabel';
import ErrorFeedback from './FormFields/ErrorFeedback';
import { debounce } from 'lodash';
import RangeDatePicker from 'screens/common/RageDatePicker';
import MonthDayRange from './FormFields/MonthDayRange';
import PasswordInput from './FormFields/Mapmodel/PasswordInput';
import offlineDBService from 'db/dexieDB';
import { getValue } from 'helpers/utils';
import FormReport from './FormFields/FormReport';
import TextAndUnitField from './FormFields/TextAndUnitField';
import CalculationField from './FormFields/CalculationField';

// Helper function to detect mobile device
function isMobileDevice() {
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}
const DynamicNormalForm = ({
  metadata,
  fields,
  onToggleChange,
  handleDateRangeChange,
  handleMonthDayChange,
  mode,
  hideFields,
  modalDataMap,
  onFormMetadataChange,
  handleSelectRow,
  selectedRows,
  onFieldChange
}) => {
  const apiKey = process.env.REACT_APP_OLA_API_KEY;
  // Use useController for more complex form control

  const {
    control,
    register,
    formState: { errors },
    watch,
    setValue,
    getValues
  } = useFormContext();

  const formValues = watch();
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const selectRef = useRef(null);
  const [address, setAddress] = useState(
    metadata?.defaultValue?.address || formValues.location?.address || false
  );
  const hasRun = useRef(false);
  const isDisabledRef = useRef(false); // Track disabled state using ref
  const [fieldMetadata, setFieldMetadata] = React.useState(metadata); // Store metadata in state
  const [showModal, setShowModal] = useState(false);
  const [uploadedGeoJSONLayer, setUploadedGeoJSONLayer] = useState(
    metadata.type == 'coordinates' ? metadata.defaultValue : null
  );
  const [locationFetched, setLocationFetched] = useState(false); // Track if the location was fetched already
  const [geoLocationCompleted, setGeoLocationCompleted] = useState(false);

  useEffect(() => {
    //to show that address in the form form below the location field
    if (formValues[metadata.id]?.address) {
      setAddress(formValues[metadata.id].address);
    }
  }, [formValues[metadata.id]?.address]);

  // Simple effect to update char_length
  useEffect(() => {
    if (metadata.type === 'char_length' && metadata.dependsOn) {
      const dependentValue = watch(metadata.dependsOn) || '';
      setValue(metadata.id, dependentValue.length);
    }
  }, [watch(metadata.dependsOn)]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (metadata.type === 'number' && metadata.dependsOn) {
      if (metadata.dependsOn === 'coordinates') {
        const dependentValue = watch(metadata.dependsOn) || {};

        if (
          dependentValue &&
          Array.isArray(dependentValue.geometry?.coordinates)
        ) {
          const polygon = turf.polygon(dependentValue?.geometry?.coordinates);
          const area = turf.area(polygon);
          const area_hectares = (area / 10000).toFixed(2);
          const area_acres = (area / 4046.8564224).toFixed(2);
          setValue(metadata.id, area_acres || 0);
        } else {
          setValue(metadata.id, 0);
        }
      }
    }
  }, [watch(metadata.dependsOn)]);

  // Add dependencies
  const handleClickOutside = event => {
    if (selectRef.current && !selectRef.current.contains(event.target)) {
      setMenuIsOpen(false);
    }
  };
  const isFieldDisabled = () => {
    // Check if the field has a `disables` array
    if (fieldMetadata.disables?.length > 0) {
      // Iterate through the disables array
      for (const disabledField of fieldMetadata.disables) {
        if (formValues[disabledField]) {
          // If any field in disables has a value, disable the field
          if (!isDisabledRef.current) {
            isDisabledRef.current = true; // Set ref to true to indicate the field should be disabled
            setFieldMetadata(prevMetadata => ({
              ...prevMetadata,
              isEditable: false // Disable the field
            }));
          }
          return true; // Field is disabled
        }
      }
    }

    // If no disabling condition is met, make sure it's editable
    if (isDisabledRef.current) {
      isDisabledRef.current = false; // Reset the ref
      setFieldMetadata(prevMetadata => ({
        ...prevMetadata,
        isEditable: true // Enable the field
      }));
    }

    return false; // Field is not disabled
  };

  useEffect(() => {
    if (metadata.type === 'char_length' && metadata.dependsOn) {
      // Subscribe to changes in the dependent field
      const subscription = watch((value, { name }) => {
        if (name === metadata.dependsOn) {
          const textLength = (value[metadata.dependsOn] || '').length;
          setValue(metadata.id, textLength.toString(), {
            shouldValidate: true
          });
        }
      });

      // Initial value setup
      const initialValue = getValues(metadata.dependsOn);
      if (initialValue) {
        setValue(metadata.id, initialValue.length.toString(), {
          shouldValidate: true
        });
      }

      return () => subscription.unsubscribe();
    }
  }, [
    metadata.type,
    metadata.dependsOn,
    watch,
    // setValue,
    getValues,
    metadata.id
  ]);

  const dependentFieldValue = getValue(metadata.id, formValues);

  // Debounce the API call to prevent rapid successive calls
  const debouncedFetchData = useCallback(
    debounce(async values => {
      try {
        const requestData = {
          farmer_group: values[0] || [],
          village: values[1] || []
        };

        const response = await apiClient.post(
          `/digitization/v1/farmer/count`,
          requestData
        );

        setValue(metadata.id, response.data.results || '0');
      } catch (error) {
        console.error('Error fetching farmer count:', error);
        setValue(metadata.id, '0');
      }
    }, 500),
    [metadata.id]
  );

  const updateFormMetadata = (fieldId, options) => {
    onFormMetadataChange(fieldId, options);
  };

  const formatGroupedOptions = options => {
    if (!options) return [];

    // Check if the options are grouped (have sectionLabel)
    if (options[0]?.sectionLabel) {
      return options.map(group => ({
        label: group.sectionLabel,
        options: group.options.map(option => ({
          value: option.value,
          label: option.label
        }))
      }));
    }

    // Return regular options if not grouped
    return options.map(option => ({
      value: option.value,
      label: option.label
    }));
  };
  const selectOptions = formatGroupedOptions(metadata.options);
  const flattenOptions = options => {
    return options.reduce((acc, group) => {
      if (group.options) {
        return [...acc, ...group.options];
      }
      return [...acc, group];
    }, []);
  };

  const flatOptions = flattenOptions(selectOptions);
  const formatValue = (options, value) => {
    console.log('value is : ', value);
    if (!value) return metadata.multiSelect ? [] : null;
    const findOption = val => flatOptions.find(opt => opt.value === val);
    let returnValue = metadata.multiSelect
      ? (Array.isArray(value) ? value : [value]).map(findOption).filter(Boolean)
      : findOption(value);
    return returnValue;
  };

  const handleFieldValueChange = useCallback(
    (fieldId, value) => {
      setValue(fieldId, value);

      // Notify parent about the change for dependency processing
      if (onFieldChange) {
        onFieldChange(fieldId, value);
      }
    },
    [setValue, onFieldChange]
  );

  // Update: fetchDependentData accepts overrideParentValue for radio group
  const fetchDependentData = async (overrideParentValue = null) => {
    // ✅ Special logic for total_farmer_count
    if (metadata.id === 'total_farmer_count') {
      const [farmerGroupValue, villageValue] = dependentFieldValue || [];

      if (!farmerGroupValue?.length && !villageValue?.length) {
        setValue(metadata.id, '');
        return;
      }

      debouncedFetchData([farmerGroupValue, villageValue]);
      return;
    }

    if (!metadata.dependents || metadata.dependents.length === 0) return;

    // Filter out modal dependencies (those with dots)
    const localDependents = metadata.dependents.filter(
      dep => !dep.includes('.')
    );

    if (localDependents.length === 0) return;

    const isOnline = navigator.onLine;
    let dependentFields = {};

    for (const childId of metadata.dependents) {
      const childField = Object.values(fields).find(f => f.id === childId);
      if (!childField) continue;

      let parentKeys = Array.isArray(childField.dependsOn)
        ? childField.dependsOn
        : [childField.dependsOn || metadata.id];

      // Use overrideParentValue if provided and this is the radio group parent
      const parentValues = parentKeys.map(key => {
        if (overrideParentValue && key === metadata.id) {
          return overrideParentValue;
        }
        return getValue(key, formValues);
      });

      dependentFields = Object.fromEntries(
        parentKeys.map((k, i) => [k, parentValues[i]])
      );

      const hasEmptyParent = parentValues.some(
        val =>
          val === null || val === '' || (Array.isArray(val) && val.length === 0)
      );
      if (hasEmptyParent && !formValues[childField.id]) continue;

      try {
        // ✅ Handle form_report separately with POST
        if (childField.type === 'form_report') {
          const queryParams = new URLSearchParams();
          parentKeys.forEach((key, i) => {
            queryParams.append(key, parentValues[i]);
          });

          // Set limit dynamically: prefer childField.limit, then childField.pagination.limit
          const dynamicLimit =
            childField.limit ||
            (childField.pagination && childField.pagination.limit) ||
            25;

          const endpoint = `${childField.endpoint}?${queryParams.toString()}`;
          const requestData = {
            cursor: 0,
            limit: dynamicLimit,
            search_text: '',
            selected_ids:
              childField?.isMulti == false
                ? childField?.defaultValue || ''
                : childField?.defaultValue || []
          };

          const response = await apiClient.post(endpoint, requestData);
          const reportData = response?.data?.results?.data;
          updateFormMetadata(childField.id, reportData);
          continue; // ✅ Skip generic logic
        }

        // ✅ Standard dependent fetch
        let endpoint = childField.optionsEndpoint || childField.endpoint;
        if (!endpoint) continue;

        // Replace {id} in endpoint
        if (!Array.isArray(parentValues) && endpoint.includes('{id}')) {
          endpoint = endpoint.replace('{id}', parentValues[0]);
        } else if (Array.isArray(parentValues) && endpoint.includes('{id}')) {
          endpoint = endpoint.replace('{id}', parentValues.join(','));
        }

        // ✅ Append query parameters if defined
        if (childField.queryParams) {
          const urlParams = new URLSearchParams(
            childField.queryParams
          ).toString();
          endpoint += endpoint.includes('?')
            ? `&${urlParams}`
            : `?${urlParams}`;
        }

        let requestData = {};
        if (childField.multiSelect && Array.isArray(parentValues[0])) {
          requestData = parentValues[0];
        } else if (childField.multiSelect) {
          requestData = { [childField.id]: parentValues };
        }

        let response;
        if (!isOnline) {
          response = await getOfflineData(
            childField,
            dependentFields,
            parentValues
          );
        } else {
          response =
            childField.multiSelect &&
            (Array.isArray(parentValues[0]) || Array.isArray(requestData))
              ? await apiClient.post(endpoint, requestData)
              : await apiClient.get(endpoint);
        }

        const options = response?.data?.results || [];

        // Handle dropdown
        if (childField.type === 'dropdown') {
          updateFormMetadata(childField.id, options);
          setValue(
            childField.id,
            formValues[childField.id] ?? childField.defaultValue ?? ''
          );
        }

        // Handle textarea
        if (childField.type === 'textarea') {
          const resultValue =
            response?.data?.results?.defaultValue || response?.data?.results;
          setValue(childField.id, resultValue);
        }
      } catch (error) {
        console.error(`Error fetching data for child ${childId}:`, error);

        if (childField.type === 'dropdown') {
          try {
            const offlineResponse = await getOfflineData(
              childField,
              dependentFields,
              parentValues
            );
            const offlineOptions = offlineResponse?.data?.results || [];

            updateFormMetadata(childField.id, offlineOptions);
            setValue(
              childField.id,
              formValues[childField.id] ?? childField.defaultValue ?? ''
            );
          } catch (offlineErr) {
            console.error(
              `Offline fallback failed for ${childId}:`,
              offlineErr
            );
            updateFormMetadata(childField.id, []);
            setValue(childField.id, childField.multiSelect ? [] : '');
          }
        }
      }
    }
  };
  // Function to get data from Dexie DB when offline
  const getOfflineData = async (
    metadata,
    dependentFields,
    dependentFieldValue
  ) => {
    try {
      // Initialize results array
      let results = [];

      // Get the field ID from metadata
      const fieldId = metadata.id;

      // Get dropdown data from Dexie
      const dropdownData = await offlineDBService.getDropdownData();

      // Handle case when no dropdown data exists
      if (!dropdownData || !dropdownData.results) {
        console.error('No dropdown data available in Dexie DB');

        // Return empty results instead of throwing error
        return {
          data: {
            success: true,
            status: 200,
            results: []
          }
        };
      }

      // Check if the field exists in dropdownData
      if (dropdownData.results[fieldId]) {
        const fieldData = dropdownData.results[fieldId];

        // If the field depends on another field
        if (metadata.dependsOn) {
          let parentId;

          // Get the parent field ID
          if (Array.isArray(metadata.dependsOn)) {
            // Use the first valid dependency value
            for (const dep of metadata.dependsOn) {
              if (dependentFields[dep]) {
                parentId = dependentFields[dep];
                break;
              }
            }
          } else {
            parentId = dependentFields[metadata.dependsOn];
          }

          // Get options based on parent ID
          if (parentId && fieldData[parentId]) {
            results = fieldData[parentId];
          } else if (fieldData['all']) {
            // If no specific parent data but we have 'all' data
            results = fieldData['all'];
          } else {
            // If parent ID not found but we have data structure,
            // use the first available parent as fallback
            if (typeof fieldData === 'object' && !Array.isArray(fieldData)) {
              const firstParentId = Object.keys(fieldData)[0];
              if (firstParentId) {
                results = fieldData[firstParentId];
              } else {
                results = [];
              }
            } else if (Array.isArray(fieldData)) {
              // If it's directly an array, use it
              results = fieldData;
            }
          }
        } else {
          // Check if it's a nested structure or direct array
          if (typeof fieldData === 'object' && !Array.isArray(fieldData)) {
            // It's a nested structure, flatten all values
            results = Object.values(fieldData).flat();
          } else if (Array.isArray(fieldData)) {
            // It's already an array
            results = fieldData;
          }
        }
      } else {
        console.log(`Field ${fieldId} not found in offline dropdown data`);
      }

      // Create a response object similar to API response
      const response = {
        data: {
          success: true,
          status: 200,
          results: results
        }
      };

      console.log(`Returning ${results.length} options for ${fieldId}`);
      return response;
    } catch (error) {
      console.error('Error in getOfflineData:', error);

      // Return empty results instead of throwing error
      return {
        data: {
          success: true,
          status: 200,
          results: []
        }
      };
    }
  };

  useEffect(() => {
    if (!geoLocationCompleted) return; // Wait until geolocation is finished
    // if (!metadata.dependents) return;
    // Only trigger if there are dependents
    if (!metadata.dependents || metadata.dependents.length === 0) return;

    // Get current field value
    const currentValue = formValues[metadata.id];

    // Skip if no value
    if (
      !currentValue ||
      currentValue === '' ||
      (Array.isArray(currentValue) && currentValue.length === 0)
    ) {
      return;
    }
    fetchDependentData(); // Fetch dependent data when geolocation is completed
  }, [
    geoLocationCompleted,
    metadata.dependents,
    formValues[metadata.id],
    metadata.id
  ]); // Add metadata.dependents to dependencies

  useEffect(() => {
    const presetLocation = async () => {
      try {
        if (locationFetched) {
          setGeoLocationCompleted(true);
          return;
        }

        if ('geolocation' in navigator) {
          navigator.geolocation.getCurrentPosition(
            async position => {
              const { latitude, longitude, accuracy } = position.coords;

              try {
                const addressDetails = await fetchAddressFromOla(
                  latitude,
                  longitude,
                  apiKey
                );

                if (metadata?.defaultValue?.lat) {
                  setValue(metadata.id, {
                    lat: metadata?.defaultValue?.lat,
                    lng: metadata?.defaultValue?.lng,
                    accuracy: metadata?.defaultValue?.accuracy,
                    address: metadata?.defaultValue?.address
                  });
                } else {
                  setValue(metadata.id, {
                    lat: latitude,
                    lng: longitude,
                    accuracy: accuracy,
                    address: addressDetails.address
                  });
                }

                setLocationFetched(true);
              } catch (error) {
                console.error('Error fetching address:', error);
              }
              setGeoLocationCompleted(true); // Mark geolocation as complete
            },
            error => {
              console.error('Error fetching location:', error);
              alert(
                'Failed to retrieve your location. Please ensure location services are enabled.'
              );
              setGeoLocationCompleted(true); // Mark geolocation as complete even on failure
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0
            }
          );
        } else {
          console.warn('Geolocation is not supported by this browser.');
          setGeoLocationCompleted(true); // Mark complete if geolocation is not available
        }
      } catch (error) {
        console.error('Error during presetLocation:', error);
        setGeoLocationCompleted(true); // Mark complete if there's an error
      }
    };

    if (
      metadata.type === 'geo_location' &&
      !locationFetched &&
      mode !== 'edit' &&
      isMobileDevice()
    ) {
      presetLocation();
    } else {
      setGeoLocationCompleted(true); // If no geolocation needed, mark as complete
      if (metadata?.defaultValue?.lat) {
        setValue(metadata.id, {
          lat: metadata?.defaultValue?.lat,
          lng: metadata?.defaultValue?.lng,
          accuracy: metadata?.defaultValue?.accuracy,
          address: metadata?.defaultValue?.address
        });
      }
    }
  }, [
    metadata.type,
    metadata.id,
    setValue,
    apiKey,
    fetchAddressFromOla,
    locationFetched
  ]);

  const { field } = useController({
    name: metadata.id,
    control,
    defaultValue: metadata?.defaultValue
  });

  const handleLocationSelect = result => {
    if (result.type === 'location') {
      setValue(metadata.id, result.data); // Set the coordinates in the form
    } else if (result.type === 'polygon') {
      if (result.data.geometry) {
        setUploadedGeoJSONLayer(result.data);
        const polygon = turf.polygon(result?.data?.geometry?.coordinates);
        const area = turf.area(polygon); // Area in square meters
        setValue(metadata.id, result.data);
      } else {
        const area = 0;
        setValue(metadata.id, {});
        setUploadedGeoJSONLayer({});
      }
    }
  };

  useEffect(() => {
    if (metadata.type === 'tableAggregation') {
      const rows = modalDataMap?.[metadata.tableId];
      // Set 0 initially only once
      if (!Array.isArray(rows)) {
        const currentValue = getValues(metadata.id);
        if (currentValue === undefined) {
          setValue(metadata.id, 0);
        }
        return;
      }
      // If dependsOn is not set, don't update total
      if (!metadata.dependsOn) return;
      // If rows exist, recalculate total based on crop_price
      const total = rows.reduce((acc, row) => {
        return acc + Number(row[metadata.dependsOn] || 0);
      }, 0);
      // Only update if total is a number
      setValue(metadata.id, isNaN(total) ? 0 : total);
    }
  }, [modalDataMap?.[metadata.tableId], getValues(metadata.dependsOn)]);

  useEffect(() => {
    if (metadata.type == 'radio_group') {
      hideFields(metadata.hide, metadata.defaultValue);
    }
  }, []); // Runs when the component mounts

  // Add a debounced search handler for FormReport
  // const debouncedFormReportSearch = useRef();
  // if (!debouncedFormReportSearch.current) {
  //   debouncedFormReportSearch.current = debounce(
  //     async (searchText, childField, parentKeys, parentValues) => {
  //       const queryParams = new URLSearchParams();
  //       parentKeys.forEach((key, i) => {
  //         queryParams.append(key, parentValues[i]);
  //       });
  //       const endpoint = `${childField.endpoint}?${queryParams.toString()}`;
  //       const requestData = {
  //         cursor: 0,
  //         limit: 10,
  //         search_text: searchText,
  //         selected_ids:
  //           childField?.isMulti == false
  //             ? childField?.defaultValue || ''
  //             : childField?.defaultValue || []
  //       };
  //       const response = await apiClient.post(endpoint, requestData);
  //       const reportData = response?.data?.results?.data;
  //       updateFormMetadata(childField.id, reportData);
  //     },
  //     400
  //   );
  // }

  const fieldsArray = Array.isArray(fields)
    ? fields
    : Object.values(fields || {});

  const formMetadata = {
    fields: fieldsArray.reduce((acc, field) => {
      if (field?.id) acc[field.id] = field;
      return acc;
    }, {})
  };

  // Render form field based on metadata type
  const renderFormField = () => {
    switch (metadata.type) {
      case 'text':
      case 'email':
      case 'number':
      case 'tableAggregation':
      case 'input': {
        return (
          <Col>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <FormLabel
                label={metadata.label}
                required={metadata?.validation?.required}
              />
              <Form.Control
                {...field}
                {...register(metadata.id, {
                  ...(metadata.type === 'number' && {
                    setValueAs: value =>
                      value === '' ? null : parseFloat(value)
                  })
                })}
                type={metadata.type}
                placeholder={metadata.placeholder}
                isInvalid={!!errors[metadata.id]}
                disabled={!fieldMetadata.isEditable}
              />
              <ErrorFeedback error={errors[metadata.id]} />
            </Form.Group>
          </Col>
        );
      }
      case 'textAndUnit': {
        return (
          <TextAndUnitField
            metadata={metadata}
            namePrefix={metadata.id}
            errors={errors}
            isDisabled={!fieldMetadata.isEditable}
          />
        );
      }
      case 'minmax': {
        return (
          <Col>
            <Form.Group
              as={Col}
              sm={12}
              md={12}
              className="mb-1"
              style={{
                border: '1px solid grey',
                borderRadius: '5px',
                padding: '10px',
                position: 'relative'
              }}
            >
              <FormLabel
                label={metadata.label}
                required={metadata.validation.required}
              />
              <Row>
                <Col sm={6}>
                  <Form.Control
                    {...register(`${metadata.id}.min`)}
                    type="number"
                    placeholder={`Min ${metadata.placeholder}`}
                    isInvalid={!!errors[metadata.id]?.min}
                    disabled={!metadata.isEditable}
                    onWheel={e => e.target.blur()}
                  />
                </Col>
                <Col sm={6}>
                  <Form.Control
                    {...register(`${metadata.id}.max`)}
                    type="number"
                    placeholder={`Max ${metadata.placeholder}`}
                    isInvalid={!!errors[metadata.id]?.max}
                    disabled={!metadata.isEditable}
                    onWheel={e => e.target.blur()}
                  />
                </Col>
              </Row>
            </Form.Group>

            {/* Display Error Message Below the Green Box */}
            {(errors[metadata.id]?.min || errors[metadata.id]?.max) && (
              <div className="text-danger small">
                {errors[metadata.id]?.min?.message ||
                  errors[metadata.id]?.max?.message}
              </div>
            )}
          </Col>
        );
      }

      case 'contact_number': {
        return (
          <Col>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <FormLabel
                label={metadata.label}
                required={metadata?.validation?.required}
              />
              <Form.Control
                {...field}
                {...register(metadata.id)}
                type={'text'}
                inputMode="numeric"
                placeholder={metadata.placeholder}
                isInvalid={!!errors[metadata.id]}
              />
              <ErrorFeedback error={errors[metadata.id]} />
            </Form.Group>
          </Col>
        );
      }

      case 'textarea': {
        const textValue = field.value || '';

        return (
          <Col>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <FormLabel
                label={metadata.label}
                required={metadata?.validation?.required}
              />
              <Form.Control
                as="textarea"
                {...field}
                value={textValue}
                onChange={e => {
                  field.onChange(e);
                  if (metadata.showCharLength) {
                    setValue(`${metadata.id}_length`, e.target.value.length);
                  }
                }}
                style={{ minHeight: '100px' }}
                placeholder={metadata.placeholder}
                isInvalid={!!errors[metadata.id]}
                disabled={!fieldMetadata.isEditable}
              />
              {metadata.showCharLength && textValue.length > 0 && (
                <div className="text-end mt-1 text-muted">
                  Characters: {textValue.length}
                </div>
              )}
              <ErrorFeedback error={errors[metadata.id]} />
            </Form.Group>
          </Col>
        );
      }
      case 'numbercalculation': {
        return <CalculationField metadata={metadata} mode={mode} />;
      }

      case 'radio_group': {
        return (
          <Col>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <FormLabel
                label={metadata.label}
                required={metadata?.validation?.required}
              />
              <div className="d-flex flex-wrap gap-3">
                {metadata.options.map(option => (
                  <Form.Check
                    key={option.value}
                    type="radio"
                    id={`${metadata.id}-${option.value}`}
                    label={option.label}
                    value={option.value}
                    disabled={!metadata.isEditable}
                    isInvalid={!!errors[metadata.id]}
                    {...register(metadata.id, {
                      onChange: e => {
                        if (metadata.hide) {
                          hideFields(metadata.hide, e.target.value);
                        }
                        // Call fetchDependentData with the new value
                        setTimeout(() => {
                          fetchDependentData(e.target.value);
                        }, 0);
                      }
                    })}
                    defaultChecked={option.value === metadata.defaultValue}
                    className="d-inline-block"
                  />
                ))}
              </div>
              <ErrorFeedback error={errors[metadata.id]} />
            </Form.Group>
          </Col>
        );
      }

      case 'dropdown': {
        // const isDisabled = isFieldDisabled(metadata.id);
        return (
          <DynamicDropDown
            key={`${metadata.id}-${metadata.options?.length ?? 0}`}
            metadata={metadata}
            control={control}
            errors={errors}
            dependentFieldValue={dependentFieldValue}
            enableSelectAll={true}
            isDisabled={isFieldDisabled()}
            onChange={value => handleFieldValueChange(metadata.id, value)}
            formMetadata={formMetadata}
          />
        );
      }
      case 'toggle_with_dropdown':
        return (
          <ToggleWithDropdown
            metadata={metadata}
            control={control}
            errors={errors}
          />
        );
      case 'toggle': {
        const normalizedValue = field.value ?? metadata.defaultValue ?? false;

        return (
          <Form.Group key={metadata.id} as={Col} sm={12} className="mb-3">
            <FormLabel
              label={metadata.label}
              required={metadata?.validation?.required}
            />
            <ButtonGroup className="d-block">
              <ToggleButton
                disabled={!fieldMetadata.isEditable}
                {...field}
                id={`${metadata.id}-false`}
                type="radio"
                className="px-3"
                name="evaluationEnabled"
                variant={normalizedValue ? 'light' : 'primary'} // No if `normalizedValue` is true
                checked={!normalizedValue}
                onChange={() => {
                  field.onChange(false) && onToggleChange(false);
                }}
              >
                No
              </ToggleButton>

              <ToggleButton
                disabled={!fieldMetadata.isEditable}
                id={`${metadata.id}-true`}
                type="radio"
                className="px-3"
                name="evaluationEnabled"
                variant={normalizedValue ? 'primary' : 'light'} // Yes if `normalizedValue` is true
                checked={normalizedValue}
                onChange={() => {
                  field.onChange(true) && onToggleChange(true);
                }}
              >
                Yes
              </ToggleButton>
            </ButtonGroup>
          </Form.Group>
        );
      }

      case 'checkbox': {
        return (
          <CheckBox
            metadata={metadata}
            register={register}
            field={{ value: [], onChange: () => {} }} // Replace with actual field value and onChange logic
            errors={errors}
          />
        );
      }
      case 'sectionHeader': {
        // Simplified char_length rendering - it's now just a display field
        return (
          <Col>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <h5>{metadata.label}</h5>
            </Form.Group>
          </Col>
        );
      }
      case 'file': {
        return (
          <File metadata={metadata} field={{ ...field }} setValue={setValue} />
        );
      }
      case 'date':
      case 'dob': {
        return (
          <Dob
            metadata={metadata}
            {...field}
            setValue={setValue}
            register={register}
            errors={errors}
            ErrorFeedback={ErrorFeedback}
          />
        );
      }
      case 'geo_location': {
        return (
          <Col key={metadata.id}>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <div className="flex justify-between items-center">
                <FormLabel
                  label={metadata.label}
                  required={metadata?.validation?.required}
                />
                <Button
                  style={{ marginLeft: '28px' }}
                  onClick={() => setShowModal(true)}
                  variant="primary"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-geo-alt-fill"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10m0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6" />
                  </svg>
                </Button>
              </div>
              {formValues[metadata.id] && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '8px',
                    maxWidth: '300px',
                    borderRadius: '6px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    backgroundColor: '#f9f9f9',
                    fontSize: '14px'
                  }}
                >
                  <div>
                    <h4
                      style={{
                        marginBottom: '6px',
                        fontSize: '16px',
                        fontWeight: '600'
                      }}
                    >
                      Pinned Location
                    </h4>
                    {formValues[metadata.id]?.accuracy > 0 && (
                      <p style={{ margin: '4px 0', color: '#555' }}>
                        Accurate to{' '}
                        <strong>{formValues[metadata.id].accuracy}m</strong>
                      </p>
                    )}
                    {address ? (
                      <p style={{ margin: '4px 0', color: '#555' }}>
                        Address: {address}
                      </p>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              )}
              {!formValues[metadata.id]?.address && (
                <ErrorFeedback error={errors[metadata.id]} />
              )}
            </Form.Group>
            <MapModel
              defaultValue={metadata?.defaultValue} //passing the default value which contain location details from backend so that the map can have dafault data
              showModal={showModal}
              setShowModal={setShowModal}
              mode="location"
              onLocationSelect={handleLocationSelect}
            />
          </Col>
        );
      }
      case 'coordinates': {
        return (
          <Col key={metadata.id}>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <FormLabel
                label={metadata.label}
                required={metadata?.validation?.required}
              />
              <Button onClick={() => setShowModal(true)} variant="primary">
                Map Farm-land
              </Button>
              {(formValues[metadata.id]?.geometry?.coordinates ||
                metadata.defaultValue?.geometry?.coordinates) && (
                <div className="mt-2">Plotted</div>
              )}

              <ErrorFeedback error={errors[metadata.id]} />
            </Form.Group>
            <MapModel
              key={metadata.id}
              showModal={showModal}
              setShowModal={setShowModal}
              mode="polygon"
              uploadedGeoJSONLayer={uploadedGeoJSONLayer}
              onLocationSelect={handleLocationSelect}
            />
          </Col>
        );
      }
      case 'date_range':
        return (
          <RangeDatePicker
            onDateChange={handleDateRangeChange}
            noLabel={false}
            styleWidth={true}
          />
        );
      case 'month_day_range':
        return (
          <MonthDayRange
            onRangeChange={handleMonthDayChange}
            styleWidth={true}
            metadata={metadata}
          />
        );
      case 'password': {
        return (
          <div>
            <PasswordInput metadata={metadata} {...field} />
          </div>
        );
      }
      case 'form_report': {
        return (
          <FormReport
            metadata={metadata}
            selectedRows={selectedRows}
            handleSelectRow={handleSelectRow}
            formValues={watch()}
            error={errors[metadata.id]}
            // onSearch={searchText => {
            //   // Only call if metadata.endpoint exists
            //   if (metadata.endpoint) {
            //     const parentKeys = Array.isArray(metadata.dependsOn)
            //       ? metadata.dependsOn
            //       : [metadata.dependsOn || metadata.id];
            //     const parentValues = parentKeys.map(key =>
            //       getValue(key, formValues)
            //     );
            //     debouncedFormReportSearch.current(
            //       searchText,
            //       metadata,
            //       parentKeys,
            //       parentValues
            //     );
            //   }
            // }}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
      {renderFormField()}
      {/* Add the MapModal here */}
    </>
  );
};

export default DynamicNormalForm;
